import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { hash, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { and, not, or, eq, lte } from 'ember-truth-helpers';
import { variation } from 'ember-launch-darkly';
import { t } from 'ember-intl';
import set from 'ember-set-helper/helpers/set';
import {
  HdsPageHeader,
  HdsIcon,
  HdsDropdown,
  HdsLinkStandalone,
  HdsStepperNav,
  HdsBadge,
  HdsCodeBlock,
  HdsTabs,
  HdsAlert,
} from '@hashicorp/design-system-components/components';
import {
  OperationRefreshRoute,
  CoreLayoutMain,
  BreadcrumbsContainer,
  FlexGrid,
  Typography,
  OperationFailureForResource,
  Loading,
  DisclosureGuide,
  Flex,
  DetailSection,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import { resourceIsBusy, urlFor, resourceTypeFor } from 'core/helpers';
import { TERRAFORM_IMPORTS } from 'core/utils/constants';
import { TYPE_CONSUL, TYPE_VAULT } from 'common/utils/cloud-resource-types';
import ConnectionStatus from 'networks-common/components/networks/cards/connection-status';
import ConnectionDetails from 'networks-common/components/networks/cards/connection-details';
import { CONNECTION_METHODS } from 'networks-common/utils/connection-methods';
import { isAwsProviderNetwork } from 'networks-common/utils/networks';
import networksConnectionInfo from 'networks-common/helpers/networks-connection-info';
import ConnectionInstructionsBanner from 'networks-common/components/networks/connection-instructions-banner';
import ConnectionDetails0 from 'networks-common/components/networks/connection-details';
import ConnectionInstructionsAccordion from 'networks-common/components/networks/connection-instructions-accordion';
import {
  isPendingAcceptance,
  hidePeeringInstructions,
  getUpNextTranslations,
  getBusyStateTranslations,
  getTargetType,
  AWS_QUICK_CREATE_LINK,
  PEERING_CREATING_STATUS,
  PEERING_PENDING_ACCEPTANCE_STATUS,
  PEERING_ACCEPTED_STATUS,
  PEERING_ACTIVE_STATUS,
  PEERING_FAILED_STATUS,
  PEERING_EXPIRED_STATUS,
  PEERING_REJECTED_STATUS,
  PEERING_DELETING_STATUS,
  getPeeringExpiration,
  isPeeringConfigurationComplete,
} from 'networks-common/utils/peerings';
import ConfigurationAccordion from 'hcp/components/page/hvn-peerings/configuration-accordion';
import listDependencies from 'hcp/helpers/list-dependencies';
import ContentTabs from 'hcp/components/page/hvn-peerings/detail/content-tabs';
import List from 'hcp/components/page/hvn-routes/list';
import DeletePeeringModal from 'hcp/components/page/hvn-peerings/delete-peering-modal';

/**
 *
 * `tab` will be the selected tab;
 * @argument tab;
 * @type {String}
 *
 */
/**
 *
 * `product` such as consul or vault;
 * @argument product;
 * @type {String}
 *
 */
/**
 *
 * `model` will be a model containing the peering detail;
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `PageHvnPeeringsDetail` renders the HVN Peerings Detail page.
 *
 *
 * ```
 * <Page::HvnPeerings::Detail
 *   @model={{model}}
 *   @tab={{tab}}
 *   @product={{product}}
 * />
 * ```
 *
 * @class PageHvnPeeringsDetail
 *
 */
export default class PageHvnPeeringsDetail extends Component {
  @service api;
  @service flashMessages;
  @service intl;
  @service operation;

  @tracked showDeleteModal = false;
  @tracked allowDelete = false;
  @tracked isOpen;

  // Guided Flow state
  @tracked isAlertCardDismissed = false;
  @tracked peeringMetadata = null;

  constructor() {
    super(...arguments);

    if (this.args.model.metadata) {
      let {
        peering: { id: peeringId },
        metadata: {
          metadata: { peering: peeringsMetadata },
        },
      } = this.args.model;

      this.peeringMetadata = peeringsMetadata[peeringId];
    }
  }

  get busyStateTranslations() {
    let translations = getBusyStateTranslations();
    let busyStateTranslations = translations[this.args.model.peering.state];
    if (busyStateTranslations) {
      for (let [key, value] of Object.entries(busyStateTranslations)) {
        // When a Peering state is "CREATING", the message translation requires targetType
        if (this.targetType && key === 'message') {
          busyStateTranslations[key] = this.intl.t(value, {
            targetType: this.targetType,
          });
        } else {
          busyStateTranslations[key] = this.intl.t(value);
        }
      }
      return busyStateTranslations;
    } else {
      return {};
    }
  }

  get isAws() {
    return isAwsProviderNetwork(this.args.model.network);
  }

  get isAwsQuickCreateLink() {
    return this.args.model.peering.sourceChannel === AWS_QUICK_CREATE_LINK;
  }

  get upNextText() {
    return getUpNextTranslations(this.args.model.peering.state);
  }

  get hidePeeringInstructions() {
    return (
      hidePeeringInstructions(this.args.model.peering.state) ||
      this.isAwsQuickCreateLink
    );
  }

  get isPendingAcceptance() {
    return isPendingAcceptance(this.args.model.peering.state);
  }

  get instructionsId() {
    return 'instructions';
  }

  get isHvnHvnPeering() {
    return Boolean(this.args.model.peering.target.hvnTarget?.hvn);
  }

  get targetType() {
    return getTargetType(this.args.model.peering);
  }

  get dependencies() {
    let dependencies = [];
    let networkDependencies = this.args.model.networkDependencies.filter(
      (dependency) =>
        dependency.type === TYPE_CONSUL || dependency.type === TYPE_VAULT
    );
    let hvnTargetDependencies = this.args.model.hvnTargetDependencies.filter(
      (dependency) =>
        dependency.type === TYPE_CONSUL || dependency.type === TYPE_VAULT
    );
    if (networkDependencies.length && hvnTargetDependencies.length) {
      dependencies.push(...networkDependencies, ...hvnTargetDependencies);
    }
    return dependencies;
  }

  get isLocked() {
    return this.dependencies?.length > 0;
  }

  get terraformImport() {
    switch (this.targetType) {
      case 'aws':
        return TERRAFORM_IMPORTS.awsPeering({
          hvnId: this.args.model.network.id,
          peeringId: this.args.model.peering.id,
        });
      case 'azure':
        return TERRAFORM_IMPORTS.azurePeering({
          hvnId: this.args.model.network.id,
          peeringId: this.args.model.peering.id,
        });
      case 'hvn':
        return TERRAFORM_IMPORTS.hvnPeering({
          hvnId: this.args.model.network.id,
          peeringId: this.args.model.peering.id,
        });
      default:
        return '';
    }
  }

  get currentStep() {
    const {
      model: {
        peering: { state, sourceChannel },
      },
    } = this.args;

    switch (state.toUpperCase()) {
      case PEERING_PENDING_ACCEPTANCE_STATUS:
        return 2;
      case PEERING_ACCEPTED_STATUS:
        return 3;
      case PEERING_ACTIVE_STATUS:
        return sourceChannel === AWS_QUICK_CREATE_LINK ? 4 : 3; // Maintaining the Configure step as a mitigation design
      default:
        return 2;
    }
  }

  get currentStepperStep() {
    return this.currentStep - 1;
  }

  get shouldShowStepper() {
    const {
      model: {
        peering: { state, sourceChannel },
      },
    } = this.args;

    let stepperStates = [
      PEERING_FAILED_STATUS,
      PEERING_EXPIRED_STATUS,
      PEERING_REJECTED_STATUS,
      PEERING_DELETING_STATUS,
    ];

    if (sourceChannel === AWS_QUICK_CREATE_LINK) {
      stepperStates = [...stepperStates, PEERING_ACTIVE_STATUS];
    }

    return !stepperStates.includes(state);
  }

  get shouldShowAlertCard() {
    const {
      model: {
        peering: { state, sourceChannel },
      },
    } = this.args;
    let alertCardStates = [PEERING_FAILED_STATUS, PEERING_EXPIRED_STATUS];

    if (
      sourceChannel === AWS_QUICK_CREATE_LINK ||
      (this.peeringMetadata &&
        isPeeringConfigurationComplete(this.peeringMetadata))
    ) {
      alertCardStates = [...alertCardStates, PEERING_ACTIVE_STATUS];
    }

    return alertCardStates.includes(state);
  }

  get isAwsCloudShell() {
    return this.args.connectionMethod === CONNECTION_METHODS.awsCloudShell.key;
  }

  get isWebConsole() {
    return this.args.connectionMethod === CONNECTION_METHODS.webConsole.key;
  }

  get expirationDate() {
    let {
      model: { peering },
    } = this.args;
    return getPeeringExpiration(peering).toLocaleString();
  }

  get isManualConnectionActive() {
    let {
      model: { peering },
    } = this.args;

    return peering.sourceChannel !== AWS_QUICK_CREATE_LINK
      ? peering.state === PEERING_ACTIVE_STATUS
      : false;
  }

  get isPeeringBeingCreated() {
    let {
      model: {
        peering: { state },
      },
    } = this.args;

    return state === PEERING_CREATING_STATUS;
  }

  get connection() {
    let {
      model: { peering },
    } = this.args;

    if (!this.peeringMetadata || peering.state !== PEERING_ACTIVE_STATUS) {
      return this.args.model.peering;
    } else {
      let state = isPeeringConfigurationComplete(this.peeringMetadata)
        ? peering.state
        : PEERING_PENDING_ACCEPTANCE_STATUS;

      return { ...peering, ...{ state } };
    }
  }

  updatePeeringMetadata = (metadata) => {
    this.peeringMetadata = metadata;
  };

  <template>
    <OperationRefreshRoute
      @route="cloud.orgs.detail.projects.detail.hvns.detail.peerings.detail"
      @routeFallback={{urlFor
        "cloud.orgs.detail.projects.detail.hvns.detail.peerings.list"
      }}
      @model={{@model.peering}}
      @resourceType={{resourceTypeFor "network-peering"}}
    />
    {{#if (and (variation "hcp-hvn-guided-flow") this.isAws)}}
      <OperationRefreshRoute
        @route="cloud.orgs.detail.projects.detail.hvns.detail.peerings.detail"
        @routeFallback={{urlFor
          "cloud.orgs.detail.projects.detail.hvns.detail.peerings.list"
        }}
        @model={{@model.network}}
        @resourceType={{resourceTypeFor "network"}}
      />
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader data-test-peering-header as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title @lg="8">
              {{@model.peering.id}}
              {{#if this.isLocked}}
                <HdsIcon
                  @name="lock-fill"
                  @color="var(--token-color-foreground-strong)"
                  @size="24"
                  @isInline={{true}}
                  {{boxPadding "0 0 0 2xs"}}
                />
              {{/if}}
            </PH.Title>
            {{#unless
              (resourceIsBusy
                @model.peering type=(resourceTypeFor "network-peering")
              )
            }}
              <PH.Actions>
                {{#unless this.isLocked}}
                  <HdsDropdown @listPosition="bottom-right" as |dd|>
                    <dd.ToggleButton
                      @color="primary"
                      @text={{t "components.form.manage"}}
                      data-test-dropdown
                    />
                    <dd.Title
                      @text={{t "components.import-to-terraform.title"}}
                    />
                    <dd.Description
                      @text={{t "components.import-to-terraform.body"}}
                    />
                    <dd.Generic>
                      <div {{boxPadding "xs 0"}}>
                        <HdsLinkStandalone
                          @href="https://www.terraform.io/docs/cli/import/usage.html"
                          @icon="docs-link"
                          @text={{t "components.import-to-terraform.docs-link"}}
                        />
                      </div>
                    </dd.Generic>
                    <dd.CopyItem @text={{this.terraformImport}} />
                    <dd.Separator />
                    <dd.Interactive
                      id="modal-dialog-open-button-{{@model.peering.id}}"
                      @color="critical"
                      @icon="trash"
                      @text={{t "components.form.delete"}}
                      data-test-delete-peering-button
                      {{on "click" (set this "showDeleteModal" true)}}
                    />
                  </HdsDropdown>
                {{/unless}}
              </PH.Actions>
            {{/unless}}
          </HdsPageHeader>
        </:header>
        <:body>
          <div class="hvnsPeeringsDetail" data-test-peering-details>
            {{#if this.shouldShowStepper}}
              <HdsStepperNav
                @currentStep={{this.currentStepperStep}}
                @isInteractive={{false}}
                @ariaLabel={{@model.peering.id}}
                {{boxPadding "0 0 lg 0"}}
                as |S|
              >
                <S.Step>
                  <:title>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.create.title"
                    }}
                  </:title>
                  <:description>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.create.description"
                    }}
                  </:description>
                </S.Step>
                <S.Step>
                  <:title>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.establish.title"
                    }}
                  </:title>
                  <:description>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.establish.description"
                    }}
                  </:description>
                </S.Step>
                <S.Step>
                  <:title>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.configure.title"
                    }}
                  </:title>
                  <:description>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.configure.description"
                    }}
                  </:description>
                </S.Step>
              </HdsStepperNav>
            {{/if}}
            {{#if this.shouldShowAlertCard}}
              <ConnectionStatus
                @state={{@model.peering.state}}
                {{boxPadding "0 0 md"}}
              />
            {{/if}}
            <ConnectionDetails @connection={{this.connection}} />
            {{#if
              (not
                (or
                  (eq @model.peering.state "FAILED")
                  (eq @model.peering.state "REJECTED")
                  (eq @model.peering.state "DELETING")
                  (eq @model.peering.state "EXPIRED")
                )
              )
            }}
              {{#if this.isAwsQuickCreateLink}}
                {{#if (lte this.currentStep 3)}}
                  <FlexGrid
                    class="hvnsPeeringsDetail__guided-flow__body"
                    {{boxPadding "2xl 0 0"}}
                    as |G|
                  >
                    <G.Item @xs="12" @sm="12" @md="10" @lg="10">
                      <Typography
                        @variant="h3"
                        class="hds-typography-display-300 hds-font-weight-bold"
                        {{boxMargin "0 0 md 0"}}
                      >
                        {{t
                          "components.networks.peerings.aws.automate.guided-flow.automate.detail.title"
                        }}
                      </Typography>
                      <ul {{boxPadding "0 md"}}>
                        <li>
                          <Typography
                            class="hds-typography-display-200 hds-foreground-primary"
                            {{boxMargin "0 0 md 0"}}
                          >
                            {{t
                              "components.networks.peerings.aws.automate.guided-flow.automate.detail.wait-minutes"
                            }}
                          </Typography>
                        </li>
                      </ul>
                      <img
                        src={{t
                          "components.networks.peerings.aws.automate.guided-flow.automate.detail.screenshot.imgSrc"
                        }}
                        width="100%"
                        alt={{t
                          "components.networks.peerings.aws.automate.guided-flow.automate.detail.screenshot.alt"
                        }}
                        data-test-networks-peerings-aws-create-automate-success-screenshot
                      />
                      <ul {{boxPadding "0 md"}}>
                        <li>
                          <Typography
                            class="hds-typography-display-200 hds-foreground-primary"
                            {{boxMargin "0 0 md 0"}}
                          >
                            {{t
                              "components.networks.peerings.aws.automate.guided-flow.automate.detail.deploy-clusters"
                            }}
                          </Typography>
                        </li>
                      </ul>
                      <div {{boxPadding "0 0 sm 0"}}>
                        <HdsBadge
                          @icon="info"
                          @text={{t
                            "components.networks.peerings.aws.automate.guided-flow.automate.detail.deleting-stack"
                          }}
                        />
                      </div>
                      <div>
                        <HdsBadge
                          class="hvnsPeeringsDetail__guided-flow__body__custom-badge"
                          @icon="clock"
                          @text={{t
                            "components.networks.peerings.aws.automate.guided-flow.automate.detail.completing-peering"
                          }}
                        />
                      </div>
                    </G.Item>
                  </FlexGrid>
                {{/if}}
              {{else if this.isAwsCloudShell}}
                {{#if (eq this.currentStep 2)}}
                  {{#if this.isPeeringBeingCreated}}
                    <div {{boxPadding "xl 0"}}>
                      <OperationFailureForResource
                        @resource={{@model.peering}}
                        @resourceType={{resourceTypeFor "network-peering"}}
                      />
                      <Loading data-test-loading as |L|>
                        <L.Elapsed />
                        <L.Header>
                          {{this.busyStateTranslations.header}}
                        </L.Header>
                        <L.Message>
                          {{this.busyStateTranslations.message}}
                        </L.Message>
                      </Loading>
                    </div>
                  {{else}}
                    <FlexGrid
                      class="hvnsPeeringsDetail__guided-flow__body"
                      {{boxPadding "2xl 0 0"}}
                      as |G|
                    >
                      <G.Item @xs="12" @sm="12" @md="12" @lg="12">
                        <Typography
                          @variant="h3"
                          class="hds-typography-display-300 hds-font-weight-bold"
                          {{boxMargin "0 0 md 0"}}
                        >
                          {{t
                            "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.title"
                          }}
                        </Typography>
                        <Typography
                          class="hds-typography-display-200 hds-foreground-primary"
                          {{boxMargin "0 0 md 0"}}
                        >
                          {{t
                            "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.aws-cli.description"
                          }}
                        </Typography>
                        <HdsLinkStandalone
                          @text={{t
                            "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.aws-cli.learn-link"
                          }}
                          @icon="learn-link"
                          @href={{t
                            "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.aws-cli.learn-link-url"
                          }}
                          @iconPosition="trailing"
                        />
                        <div {{boxPadding "md 0 0"}}>
                          <HdsCodeBlock
                            @language="bash"
                            @value={{t
                              "components.page.hvn-peerings.detail.instructions.aws.pending-acceptance.terminal.command"
                              connectionId=@model.peering.providerPeeringId
                              region=@model.peering.target.awsTarget.region
                            }}
                            @hasCopyButton={{true}}
                          />
                        </div>
                        {{#if @model.peering}}
                          <div {{boxPadding "2xl 0 sm"}}>
                            <HdsBadge
                              @text={{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.warning-badge"
                                expirationDate=this.expirationDate
                              }}
                              @icon="alert-triangle"
                              @color="warning"
                            />
                          </div>
                        {{/if}}
                        <div>
                          <HdsBadge
                            class="hvnsPeeringsDetail__guided-flow__body__custom-badge"
                            @icon="clock"
                            @text={{t
                              "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.aws-cli.wait-time"
                            }}
                          />
                        </div>
                      </G.Item>
                    </FlexGrid>
                  {{/if}}
                {{else if (eq this.currentStep 3)}}
                  <ConfigurationAccordion
                    @model={{@model}}
                    @connectionMethods={{hash
                      isAwsCloudShell=this.isAwsCloudShell
                      isWebConsole=this.isWebConsole
                    }}
                    @product={{@product}}
                    @peeringMetadata={{this.peeringMetadata}}
                    @updatePeeringMetadata={{this.updatePeeringMetadata}}
                  />
                {{/if}}
              {{else if this.isWebConsole}}
                {{#if (eq this.currentStep 2)}}
                  {{#if this.isPeeringBeingCreated}}
                    <div {{boxPadding "xl 0"}}>
                      <OperationFailureForResource
                        @resource={{@model.peering}}
                        @resourceType={{resourceTypeFor "network-peering"}}
                      />
                      <Loading data-test-loading as |L|>
                        <L.Elapsed />
                        <L.Header>
                          {{this.busyStateTranslations.header}}
                        </L.Header>
                        <L.Message>
                          {{this.busyStateTranslations.message}}
                        </L.Message>
                      </Loading>
                    </div>
                  {{else}}
                    <FlexGrid
                      class="hvnsPeeringsDetail__guided-flow__body"
                      {{boxPadding "2xl 0 0"}}
                      as |G|
                    >
                      <G.Item @xs="12" @sm="12" @md="10" @lg="10">
                        <Typography
                          @variant="h3"
                          class="hds-typography-display-300 hds-font-weight-bold"
                          {{boxMargin "0 0 md 0"}}
                        >
                          {{t
                            "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.title"
                          }}
                        </Typography>
                        <Typography
                          class="hds-typography-display-200 hds-foreground-primary"
                          {{boxMargin "0 0 md 0"}}
                        >
                          {{t
                            "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.web-console.description"
                          }}
                        </Typography>
                        <HdsLinkStandalone
                          @text={{t
                            "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.web-console.learn-link"
                          }}
                          @icon="learn-link"
                          @iconPosition="trailing"
                          @href={{t
                            "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.web-console.learn-link-url"
                          }}
                          {{boxPadding "0 0 sm"}}
                        />
                        <DisclosureGuide {{boxPadding "0 0 md"}} />
                        <div {{boxPadding "sm" "0"}}>
                          {{#let
                            (networksConnectionInfo @model.peering)
                            as |destination|
                          }}
                            <HdsBadge
                              @icon="info"
                              @text={{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.web-console.info-badge"
                                destinationVpc=destination.vpcId
                              }}
                            />
                          {{/let}}
                        </div>
                        {{#if @model.peering}}
                          <div>
                            <HdsBadge
                              @icon="alert-triangle"
                              @color="warning"
                              @text={{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.warning-badge"
                                expirationDate=this.expirationDate
                              }}
                            />
                          </div>
                        {{/if}}
                        <div {{boxPadding "sm 0"}}>
                          <HdsBadge
                            class="hvnsPeeringsDetail__guided-flow__body__custom-badge"
                            @icon="clock"
                            @text={{t
                              "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.web-console.wait-time"
                            }}
                          />
                        </div>
                      </G.Item>
                    </FlexGrid>
                  {{/if}}
                {{else if (eq this.currentStep 3)}}
                  <ConfigurationAccordion
                    @model={{@model}}
                    @connectionMethods={{hash
                      isAwsCloudShell=this.isAwsCloudShell
                      isWebConsole=this.isWebConsole
                    }}
                    @product={{@product}}
                    @peeringMetadata={{this.peeringMetadata}}
                    @updatePeeringMetadata={{this.updatePeeringMetadata}}
                  />
                {{/if}}
              {{else}}
                {{#if (eq this.currentStep 2)}}
                  {{#if this.isPeeringBeingCreated}}
                    <div {{boxPadding "xl 0"}}>
                      <OperationFailureForResource
                        @resource={{@model.peering}}
                        @resourceType={{resourceTypeFor "network-peering"}}
                      />
                      <Loading data-test-loading as |L|>
                        <L.Elapsed />
                        <L.Header>
                          {{this.busyStateTranslations.header}}
                        </L.Header>
                        <L.Message>
                          {{this.busyStateTranslations.message}}
                        </L.Message>
                      </Loading>
                    </div>
                  {{else}}
                    <HdsTabs
                      data-configuration-accordion-hds-tabs
                      {{boxPadding "lg 0 0"}}
                      as |T|
                    >
                      <T.Tab>
                        <Flex @alignItems="center" @gap="2xs">
                          <HdsIcon @name="aws-color" @isInline={{true}} />
                          <span>
                            {{t
                              "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.aws-cli.title"
                            }}
                          </span>
                        </Flex>
                      </T.Tab>
                      <T.Tab>
                        <Flex @alignItems="center" @gap="2xs">
                          <HdsIcon @name="hashicorp-color" @isInline={{true}} />
                          <span>
                            {{t
                              "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.title"
                            }}
                          </span>
                        </Flex>
                      </T.Tab>
                      <T.Panel>
                        <FlexGrid
                          class="hvnsPeeringsDetail__guided-flow__body"
                          {{boxPadding "lg 0 0"}}
                          as |G|
                        >
                          <G.Item @xs="12" @sm="12" @md="12" @lg="12">
                            <Typography
                              @variant="h3"
                              class="hds-typography-display-300 hds-font-weight-bold"
                              {{boxMargin "0 0 md 0"}}
                            >
                              {{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.title"
                              }}
                            </Typography>
                            <Typography
                              class="hds-typography-display-200 hds-foreground-primary"
                              {{boxMargin "0 0 md 0"}}
                            >
                              {{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.aws-cli.description"
                              }}
                            </Typography>
                            <HdsLinkStandalone
                              @text={{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.aws-cli.learn-link"
                              }}
                              @icon="learn-link"
                              @href={{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.aws-cli.learn-link-url"
                              }}
                              @iconPosition="trailing"
                            />
                            <div {{boxPadding "md 0 0"}}>
                              <HdsCodeBlock
                                @language="bash"
                                @value={{t
                                  "components.page.hvn-peerings.detail.instructions.aws.pending-acceptance.terminal.command"
                                  connectionId=@model.peering.providerPeeringId
                                  region=@model.peering.target.awsTarget.region
                                }}
                                @hasCopyButton={{true}}
                              />
                            </div>
                            {{#if @model.peering}}
                              <div {{boxPadding "2xl 0 sm"}}>
                                <HdsBadge
                                  @text={{t
                                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.warning-badge"
                                    expirationDate=this.expirationDate
                                  }}
                                  @icon="alert-triangle"
                                  @color="warning"
                                />
                              </div>
                            {{/if}}
                            <div>
                              <HdsBadge
                                class="hvnsPeeringsDetail__guided-flow__body__custom-badge"
                                @icon="clock"
                                @text={{t
                                  "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.aws-cli.wait-time"
                                }}
                              />
                            </div>
                          </G.Item>
                        </FlexGrid>
                      </T.Panel>
                      <T.Panel>
                        <FlexGrid
                          class="hvnsPeeringsDetail__guided-flow__body"
                          {{boxPadding "lg 0 0"}}
                          as |G|
                        >
                          <G.Item @xs="12" @sm="12" @md="10" @lg="10">
                            <Typography
                              @variant="h3"
                              class="hds-typography-display-300 hds-font-weight-bold"
                              {{boxMargin "0 0 md 0"}}
                            >
                              {{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.title"
                              }}
                            </Typography>
                            <Typography
                              class="hds-typography-display-200 hds-foreground-primary"
                              {{boxMargin "0 0 md 0"}}
                            >
                              {{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.web-console.description"
                              }}
                            </Typography>
                            <HdsLinkStandalone
                              @text={{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.web-console.learn-link"
                              }}
                              @icon="learn-link"
                              @iconPosition="trailing"
                              @href={{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.web-console.learn-link-url"
                              }}
                              {{boxPadding "0 0 sm"}}
                            />
                            <DisclosureGuide {{boxPadding "0 0 md"}} />
                            <div {{boxPadding "sm" "0"}}>
                              {{#let
                                (networksConnectionInfo @model.peering)
                                as |destination|
                              }}
                                <HdsBadge
                                  @icon="info"
                                  @text={{t
                                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.web-console.info-badge"
                                    destinationVpc=destination.vpcId
                                  }}
                                />
                              {{/let}}
                            </div>
                            {{#if @model.peering}}
                              <div>
                                <HdsBadge
                                  @icon="alert-triangle"
                                  @color="warning"
                                  @text={{t
                                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.warning-badge"
                                    expirationDate=this.expirationDate
                                  }}
                                />
                              </div>
                            {{/if}}
                            <div {{boxPadding "sm 0"}}>
                              <HdsBadge
                                class="hvnsPeeringsDetail__guided-flow__body__custom-badge"
                                @icon="clock"
                                @text={{t
                                  "components.networks.peerings.aws.manual.guided-flow.manual.detail.establish.web-console.wait-time"
                                }}
                              />
                            </div>
                          </G.Item>
                        </FlexGrid>
                      </T.Panel>
                    </HdsTabs>
                  {{/if}}
                {{else if (eq this.currentStep 3)}}
                  <ConfigurationAccordion
                    @model={{@model}}
                    @connectionMethods={{hash
                      isAwsCloudShell=this.isAwsCloudShell
                      isWebConsole=this.isWebConsole
                    }}
                    @product={{@product}}
                  />
                {{/if}}
              {{/if}}
            {{/if}}
          </div>
        </:body>
      </CoreLayoutMain>
    {{else}}
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader data-test-peering-header as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title>
              {{@model.peering.id}}
              {{#if this.isLocked}}
                <HdsIcon
                  @name="lock-fill"
                  @color="var(--token-color-foreground-strong)"
                  @size="24"
                  @isInline={{true}}
                  {{boxPadding "0 0 0 2xs"}}
                />
              {{/if}}
            </PH.Title>
            {{#unless
              (resourceIsBusy
                @model.peering type=(resourceTypeFor "network-peering")
              )
            }}
              <PH.Actions>
                {{#unless this.isLocked}}
                  <HdsDropdown @listPosition="bottom-right" as |dd|>
                    <dd.ToggleButton
                      @color="secondary"
                      @text={{t "components.form.manage"}}
                      data-test-dropdown
                    />
                    <dd.Title
                      @text={{t "components.import-to-terraform.title"}}
                    />
                    <dd.Description
                      @text={{t "components.import-to-terraform.body"}}
                    />
                    <dd.Interactive
                      @href="https://www.terraform.io/docs/cli/import/usage.html"
                      @icon="docs-link"
                      @isHrefExternal={{true}}
                      @text={{t "components.import-to-terraform.docs-link"}}
                    />
                    <dd.CopyItem @text={{this.terraformImport}} />
                    <dd.Separator />
                    <dd.Interactive
                      id="modal-dialog-open-button-{{@model.peering.id}}"
                      @color="critical"
                      @icon="trash"
                      @text={{t "components.form.delete"}}
                      data-test-delete-peering-button
                      {{on "click" (set this "showDeleteModal" true)}}
                    />
                  </HdsDropdown>
                {{/unless}}
              </PH.Actions>
            {{/unless}}
          </HdsPageHeader>
        </:header>
        <:body>
          {{#if
            (resourceIsBusy
              @model.peering type=(resourceTypeFor "network-peering")
            )
          }}
            <div {{boxPadding "xl 0"}}>
              <OperationFailureForResource
                @resource={{@model.peering}}
                @resourceType={{resourceTypeFor "network-peering"}}
              />
              <Loading data-test-loading as |L|>
                <L.Elapsed />
                <L.Header>
                  {{this.busyStateTranslations.header}}
                </L.Header>
                <L.Message>
                  {{this.busyStateTranslations.message}}
                </L.Message>
              </Loading>
            </div>
          {{else}}
            <div class="hvnsPeeringsDetail" data-test-peering-details>
              {{#if this.isLocked}}
                <HdsAlert
                  @type="inline"
                  data-test-locked-info
                  {{boxMargin "0 0 lg 0"}}
                  as |A|
                >
                  <A.Description>
                    {{t
                      "components.page.hvn-peerings.detail.locked-info"
                      dependencies=(listDependencies this.dependencies)
                      htmlSafe=true
                    }}
                  </A.Description>
                </HdsAlert>
              {{/if}}
              <ConnectionInstructionsBanner
                @connection={{@model.peering}}
                @id={{this.instructionsId}}
                @routes={{@model.routes}}
              />
              <FlexGrid as |G|>
                <G.Item @xs="12" @sm="12" @md="12" @lg="6">
                  <DetailSection
                    @title={{t
                      "components.page.hvn-peerings.detail.peering-details.title"
                    }}
                  >
                    <ConnectionDetails0 @connection={{@model.peering}} />
                  </DetailSection>
                </G.Item>
                {{! do not show instructions if HVN-HVN peering EXPIRED REJECTED OR FAILED }}
                {{#unless
                  (or this.isHvnHvnPeering this.hidePeeringInstructions)
                }}
                  <G.Item @xs="12" @sm="12" @md="12" @lg="12">
                    {{! template-lint-disable no-duplicate-id }}
                    <ConnectionInstructionsAccordion
                      @connection={{@model.peering}}
                      @id={{this.instructionsId}}
                      @isInitiallyOpen={{true}}
                    >
                      <ContentTabs
                        @model={{@model}}
                        @tab={{@tab}}
                        @product={{@product}}
                      />
                    </ConnectionInstructionsAccordion>
                  </G.Item>
                {{/unless}}
              </FlexGrid>
              {{#let (get this.alerts @model.peering.state) as |alert|}}
                {{#unless (eq alert.variant "warning")}}
                  <div {{boxPadding "xl 0 0 0"}}>
                    <hr />
                  </div>
                {{/unless}}
              {{/let}}
            </div>
            {{#if this.isAwsQuickCreateLink}}
              {{#if (variation "hcp-consul-activation-phase-2")}}
                <div {{boxPadding "xl 0"}}>
                  <Typography
                    @variant="h4"
                    class="hds-typography-display-200 hds-font-weight-semibold hds-foreground-primary"
                    {{boxPadding "0 0 sm 0"}}
                  >
                    {{t this.upNextText.header}}
                  </Typography>
                  <Typography
                    class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                  >
                    {{t this.upNextText.message}}
                  </Typography>
                </div>
              {{/if}}
            {{else}}
              {{! HVN Routes Table List }}
              <List
                @isHvnHvnPeering={{this.isHvnHvnPeering}}
                @model={{@model}}
              />
            {{/if}}
          {{/if}}
        </:body>
      </CoreLayoutMain>
    {{/if}}
    {{! Delete Peering Connection }}
    <DeletePeeringModal
      @returnFocusTo="modal-dialog-open-button-{{@model.peering.id}}"
      @model={{@model}}
      @peering={{@model.peering}}
      @isOpen={{this.showDeleteModal}}
      @openStateChanged={{set this "showDeleteModal"}}
    />
  </template>
}
